import { FC, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useFormContext, useWatch } from 'react-hook-form';
import { skipToken } from '@reduxjs/toolkit/query';
import { DndContext, MouseSensor, useSensor, useSensors } from '@dnd-kit/core';

import { useGetSubFoldersQuery, useGetFolderAssetsQuery } from '../../../services';
import {
  FolderManagerMimeTypes,
  FolderManagerItemActionNames,
  FolderManagerItemActionTypes,
} from '../../../interfaces';
import { usePlacematContext } from '../../Workspaces/Pages/PlacematGeneratorPage/PlacematContext';
import { FolderRow } from '../../FolderManager/Components/FolderManagerFoldersView/components/FolderRow';
import { FolderManagerGridItem } from '../../FolderManager/Components/FolderManagerGridItem';
import { isAsset } from '../../FolderManager/Components/FolderManagerFoldersView/components/FolderRow';

const parseFolderId = (sourcePath: string): number | null => {
  const match = sourcePath?.match(/crn:folders:(\d+)/);
  return match ? parseInt(match[1], 10) : null;
};

const EMPTY_SELECTED_ROWS: any[] = [];

const ASSET_PICKER_ACTIONS_CONFIG = {
  [FolderManagerMimeTypes.FOLDER]: [],
  [FolderManagerMimeTypes.PPTX]: [
    { name: FolderManagerItemActionNames.ADD_TO_PRESENTATION, type: FolderManagerItemActionTypes.MULTI },
  ],
  [FolderManagerMimeTypes.SLIDE]: [],
};

const StyledEmptyState = styled.div`
  padding: 24px 16px;
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
  line-height: 1.5;
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 12px 0;
`;

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const StyledActiveSlotLabel = styled.p`
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin: 0;
`;

const StyledClearSlotButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.danger ?? '#dc2626'};
  cursor: pointer;

  &:hover {
    opacity: 0.75;
  }
`;

const StyledRootAssetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px;
  padding: 4px 0 8px;
`;

export interface AssetPickerInputProps {
  data: {
    name: string;
    inputOptions: {
      sourcePath: string;
      unique?: boolean;
    };
  };
  prefix?: string;
  noTargetMessage?: string | null;
}

export const AssetPickerInput: FC<AssetPickerInputProps> = ({ data, prefix = '', noTargetMessage }) => {
  const { control, setValue } = useFormContext();
  const { activeSlot } = usePlacematContext();
  const inputName = prefix + data.name;

  // activationConstraint is required to allow click events to reach items —
  // without it the default PointerSensor activates immediately on pointerdown and eats the click.
  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 10 } }));

  const fieldValue: Record<string, number> = useWatch({ control, name: inputName }) ?? {};
  const allFormValues = useWatch({ control });

  const rootFolderId = parseFolderId(data.inputOptions?.sourcePath);

  const { data: rootSubFolders, isLoading: foldersLoading } = useGetSubFoldersQuery(
    rootFolderId ? { parentId: rootFolderId } : skipToken,
  );
  const { data: rootAssets, isLoading: assetsLoading } = useGetFolderAssetsQuery(
    rootFolderId ? { parentId: rootFolderId } : skipToken,
  );

  const isLoading = foldersLoading || assetsLoading;
  const selectedItemBySlotRef = useRef<Record<string, FolderManagerItem>>({});

  const selectedRows = useMemo<any[]>(() => {
    const activeAssetId = activeSlot ? fieldValue[activeSlot] : null;

    if (!activeAssetId) return EMPTY_SELECTED_ROWS;

    return activeSlot ? [{ type: FolderManagerMimeTypes.PPTX, id: fieldValue[activeSlot] }] : [];
  }, [activeSlot, fieldValue, rootAssets]);

  const disabledIds = useMemo<Set<number> | undefined>(() => {
    if (!data.inputOptions?.unique) return undefined;
    return new Set<number>(
      Object.values(allFormValues as Record<string, Record<string, Record<string, number>>>)
        .flatMap((pageValues) => Object.values(pageValues?.[data.name] ?? {}))
        .filter((id): id is number => id != null),
    );
  }, [data.inputOptions?.unique, data.name, allFormValues]);

  const latestRef = useRef({ activeSlot, fieldValue, inputName });
  latestRef.current = { activeSlot, fieldValue, inputName };

  const handleSelect = useCallback(
    (data: any) => {
      const { activeSlot, fieldValue, inputName } = latestRef.current;
      if (!activeSlot) {
        return;
      }
      const item = data?.item;
      if (!item) {
        return;
      }
      selectedItemBySlotRef.current = { ...selectedItemBySlotRef.current, [activeSlot]: item };
      setValue(inputName, { ...fieldValue, [activeSlot]: item.id }, { shouldDirty: true, shouldValidate: true });
    },
    [setValue],
  );

  const noOp = useCallback(() => {}, []);

  const handleAction = useCallback(
    (data: any) => {
      if (data?.actionType === FolderManagerItemActionNames.ADD_TO_PRESENTATION) {
        const item = data.payload?.selectedItems?.[0];
        if (!item) {
          return;
        }

        const { activeSlot, fieldValue, inputName } = latestRef.current;
        if (!activeSlot) {
          return;
        }
        setValue(inputName, { ...fieldValue, [activeSlot]: item.id }, { shouldDirty: true, shouldValidate: true });
      }
    },
    [setValue],
  );

  if (isLoading) {
    return <StyledEmptyState>Loading assets…</StyledEmptyState>;
  }

  if (noTargetMessage) {
    return <StyledEmptyState>{noTargetMessage}</StyledEmptyState>;
  }

  if (!rootFolderId) {
    return <StyledEmptyState>No source folder configured for this field.</StyledEmptyState>;
  }

  const rootFolderItems = rootSubFolders ?? [];
  // This picker always treats PPTX assets as decks (it configures PPTX/SLIDE actions explicitly),
  // regardless of whether the backend has flagged the containing folder as a slide repository.
  const rootAssetItems = (rootAssets ?? []).filter((item) => isAsset(item, true));
  const hasContent = rootFolderItems.length > 0 || rootAssetItems.length > 0;

  if (!hasContent) {
    return <StyledEmptyState>No assets found in this folder.</StyledEmptyState>;
  }

  return (
    <>
      <StyledHeader>
        <StyledActiveSlotLabel>
          {activeSlot ? `Assigning to: ${activeSlot}` : 'Select a slot in the layout, then click an asset'}
        </StyledActiveSlotLabel>

        {activeSlot && fieldValue[activeSlot] && (
          <StyledClearSlotButton
            type="button"
            onClick={() => {
              const { activeSlot, fieldValue, inputName } = latestRef.current;
              const { [activeSlot!]: _removed, ...next } = fieldValue;
              setValue(inputName, next, { shouldDirty: true, shouldValidate: true });
            }}
          >
            Clear slot
          </StyledClearSlotButton>
        )}
      </StyledHeader>

      <DndContext sensors={sensors}>
        <StyledContent>
          {rootFolderItems.map((folder) => (
            <FolderRow
              key={folder.id}
              item={folder}
              depth={0}
              selectedRows={selectedRows}
              isParentSlideRepositoryFolder
              enableDnD={false}
              disableUpload={true}
              disableSelectAll={false}
              compact
              maxGridCols={3}
              addButtonTooltip="Add"
              actionsConfig={ASSET_PICKER_ACTIONS_CONFIG}
              disabledIds={disabledIds}
              onAction={handleAction}
              onSelect={handleSelect}
              onDoubleClick={noOp}
            />
          ))}

          {rootAssetItems.length > 0 && (
            <StyledRootAssetGrid>
              {rootAssetItems.map((asset) => (
                <FolderManagerGridItem
                  key={asset.id}
                  item={asset}
                  selectedRows={selectedRows}
                  compact
                  isUploadDisabled
                  disabled={disabledIds?.has(asset.id)}
                  onSelect={handleSelect}
                  onDoubleClick={noOp}
                />
              ))}
            </StyledRootAssetGrid>
          )}
        </StyledContent>
      </DndContext>
    </>
  );
};
